/**
 * Notify Stock - Same size recommendations (popup module)
 *
 * Strategy:
 * - Determine the selected size (system + value) from the notify button dataset (preferred).
 * - Fetch candidate products via model search + Shopify recommendations API.
 * - Keep products that have an in-stock variant matching the selected size.
 * - If none found, show an empty state (instead of silently hiding).
 */

(function() {
  'use strict';

  const DEBUG = (() => {
    try {
      return window.localStorage && window.localStorage.getItem('t4sNotifyRecoDebug') === '1';
    } catch (e) {
      return false;
    }
  })();

  const log = {
    info: (...args) => DEBUG && console.log('[notify-reco]', ...args),
    warn: (...args) => DEBUG && console.warn('[notify-reco]', ...args),
    error: (...args) => console.error('[notify-reco]', ...args)
  };

  const CONFIG = {
    maxCards: 6,
    recommendationsLimit: 12,
    suggestLimit: 24,
    batchSize: 4,
    debounceMs: 120
  };

  let debounceTimer = null;
  let requestCounter = 0;
  const cache = new Map();
  let lastNotifyLoadedCount = null;

  const VERSION = '2026-01-14.3';

  // OpenPanel 追踪
  function trackEvent(eventName, properties = {}, useBeacon = false) {
    try {
      if (window.op && typeof window.op === 'function') {
        window.op('track', eventName, properties);
        log.info('tracked', eventName, properties);
      }
      
      // 使用 Beacon API 确保页面跳转时追踪不丢失
      if (useBeacon && navigator.sendBeacon) {
        const payload = {
          name: eventName,
          properties: {
            ...properties,
            page_url: window.location.href,
            timestamp: new Date().toISOString()
          }
        };
        // 发送到 OpenPanel 的 Beacon 端点（如果存在）
        // 同时写入 localStorage 作为备份，供下次页面加载时补发
        try {
          const pendingEvents = JSON.parse(localStorage.getItem('t4s_pending_events') || '[]');
          pendingEvents.push(payload);
          // 只保留最近 20 条
          if (pendingEvents.length > 20) pendingEvents.shift();
          localStorage.setItem('t4s_pending_events', JSON.stringify(pendingEvents));
        } catch (storageErr) {
          log.warn('localStorage backup failed', storageErr);
        }
        log.info('beacon queued', eventName, properties);
      }
    } catch (e) {
      log.warn('track failed', e);
    }
  }
  
  // 页面加载时补发之前未成功的事件
  function flushPendingEvents() {
    try {
      const pendingEvents = JSON.parse(localStorage.getItem('t4s_pending_events') || '[]');
      if (pendingEvents.length === 0) return;
      
      for (const evt of pendingEvents) {
        if (window.op && typeof window.op === 'function') {
          window.op('track', evt.name, evt.properties);
          log.info('flushed pending event', evt.name);
        }
      }
      localStorage.removeItem('t4s_pending_events');
    } catch (e) {
      log.warn('flush pending events failed', e);
    }
  }

  // 暴露追踪函数供其他模块使用
  window.T4SNotifyTrack = trackEvent;

  function scheduleRefresh() {
    const els = getRecoElements();
    if (els && (els.root.hidden || !els.list.innerHTML.trim())) {
      els.root.hidden = false;
      renderSkeleton(els.list, els);
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      refresh().catch(err => log.error('refresh failed', err));
    }, CONFIG.debounceMs);
  }

  function getBaseUrl() {
    const notifyButtons = Array.from(document.querySelectorAll('[data-notify-stock-btn]'));
    const btnWithBase = notifyButtons.find(b => (b.dataset && b.dataset.baseUrl) || b.getAttribute('data-base-url'));
    const baseUrl = btnWithBase?.dataset.baseUrl || btnWithBase?.getAttribute('data-base-url');

    if (baseUrl) {
      try {
        const url = new URL(baseUrl, window.location.origin);
        if (!url.pathname.endsWith('/')) url.pathname += '/';
        return url.toString();
      } catch (e) {}
    }

    const rootUrl = window.T4Sroutes && typeof window.T4Sroutes.root_url === 'string'
      ? window.T4Sroutes.root_url
      : '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    return new URL(normalizedRoot, window.location.origin).toString();
  }

  function getPopupRoot() {
    return document.getElementById('t4s-pr-popup__notify-stock');
  }

  function getRecoElements() {
    const popup = getPopupRoot();
    if (!popup) return null;

    const root = popup.querySelector('[data-oos-reco]');
    if (!root) return null;

    const list = root.querySelector('[data-oos-reco-list]');
    if (!list) return null;

    return {
      root,
      list,
      ctaText: (root.dataset.recoCta || '').trim() || 'View',
      emptyText: (root.dataset.recoEmpty || '').trim() || 'No matching items in this size right now.',
      moreText: (root.dataset.recoMoreText || '').trim() || 'Browse more'
    };
  }

  function renderSkeleton(listEl) {
    listEl.innerHTML = [
      '<div class="t4s-notify-reco__skeleton"></div>',
      '<div class="t4s-notify-reco__skeleton"></div>'
    ].join('');
  }

  function renderEmpty(listEl, text) {
    listEl.innerHTML = `<div class="t4s-notify-reco__empty">${escapeHtml(text)}</div>`;
  }

  function notifyTrackerLoaded(count) {
    if (!window.NotifyTracker) return;
    if (count === lastNotifyLoadedCount) return;
    lastNotifyLoadedCount = count;
    window.NotifyTracker.onRecoLoaded(count);
  }

  function normalizeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\u2018\u2019\u201C\u201D'"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function extractColorKeyFromTitle(title) {
    const safeTitle = String(title || '').trim();
    if (!safeTitle) return null;

    const quoteMatches = [...safeTitle.matchAll(/["\u201C\u201D\u2018\u2019']([^"\u201C\u201D\u2018\u2019']+)["\u201C\u201D\u2018\u2019']/g)];
    if (quoteMatches.length > 0) {
      const last = quoteMatches[quoteMatches.length - 1][1];
      return last ? String(last).trim() : null;
    }

    const dashParts = safeTitle.split(' - ');
    if (dashParts.length > 1) return dashParts[dashParts.length - 1].trim();

    return null;
  }

  function extractModelKeyFromTitle(title) {
    const safeTitle = String(title || '').trim();
    if (!safeTitle) return '';

    const withoutQuotes = safeTitle.replace(/["\u201C\u201D\u2018\u2019'][^"\u201C\u201D\u2018\u2019']+["\u201C\u201D\u2018\u2019']/g, '').trim();
    return withoutQuotes.replace(/\s+-\s+.+$/, '').replace(/\s{2,}/g, ' ').trim();
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function parseSizeFromText(text) {
    const t = String(text || '').trim();
    if (!t) return null;

    let m = t.match(/^([\d.]+)\s*cm$/i);
    if (m) return { system: 'CM', size: m[1] };

    m = t.match(/^([\d.]+)\s*\"$/);
    if (m) return { system: 'IN', size: m[1] };

    m = t.match(/^(US-?M|US-?W|UK|EU)\s*([\d.]+)$/i);
    if (m) {
      const rawSystem = m[1].toUpperCase();
      const system = rawSystem === 'USM' ? 'US-M' : rawSystem === 'USW' ? 'US-W' : rawSystem;
      return { system, size: m[2] };
    }

    return null;
  }

  function getNotifyButtonBestEffort() {
    const buttons = Array.from(document.querySelectorAll('[data-notify-stock-btn]'));
    if (buttons.length === 0) return null;

    const scored = buttons
      .map(btn => {
        const productId = btn.dataset.productId || btn.getAttribute('data-product-id') || '';
        const handle = btn.dataset.productHandle || btn.getAttribute('data-product-handle') || '';
        const selectedSize = btn.dataset.selectedSize || btn.getAttribute('data-selected-size') || '';
        const score =
          (productId ? 4 : 0) +
          (handle ? 2 : 0) +
          (selectedSize ? 1 : 0);
        return { btn, score };
      })
      .sort((a, b) => b.score - a.score);

    return scored[0]?.btn || buttons[0];
  }

  function getSelectedSizeContext() {
    const btn = getNotifyButtonBestEffort();
    if (btn) {
      const rawSize = btn.dataset.selectedSizeRaw || btn.getAttribute('data-selected-size-raw') || '';
      const rawSystem = btn.dataset.selectedSizeSystem || btn.getAttribute('data-selected-size-system') || '';
      if (rawSize && rawSystem) return { system: String(rawSystem).toUpperCase(), size: String(rawSize).trim() };

      const formatted = btn.dataset.selectedSize || btn.getAttribute('data-selected-size') || '';
      const parsedFormatted = parseSizeFromText(formatted);
      if (parsedFormatted) return parsedFormatted;
    }

    const popup = getPopupRoot();
    const wrapper = popup?.querySelector('.t4s-notify-wrapper');
    const variantTitle = wrapper?.dataset.variantTitle || '';

    if (window.sizeConverter && variantTitle) {
      const parsed = window.sizeConverter.parseOptionValue(variantTitle);
      if (parsed) {
        if (parsed['US-M']) return { system: 'US-M', size: String(parsed['US-M']).trim() };
        if (parsed['US-W']) return { system: 'US-W', size: String(parsed['US-W']).trim() };
      }
    }

    const sizeDisplay = popup?.querySelector('[data-size-display]');
    const parsedDisplay = parseSizeFromText(sizeDisplay?.textContent || '');
    if (parsedDisplay) return parsedDisplay;

    return null;
  }

  function isSupportedSizeSystem(system) {
    const key = String(system || '').toUpperCase();
    if (!key) return false;
    if (window.SIZE_SYSTEMS && window.SIZE_SYSTEMS[key]) return true;
    return ['US-M', 'US-W', 'UK', 'EU', 'CM', 'IN'].includes(key);
  }

  function toSizedImageUrl(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  }

  function formatMoneySafe(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';

    const moneyFormat = window.T4Sconfigs?.moneyFormat;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && moneyFormat) {
      try {
        return Shopify.formatMoney(n, moneyFormat);
      } catch (e) {}
    }
    // Fallback: add currency symbol
    const currency = window.Shopify?.currency?.active || window.T4Sconfigs?.currencyCode || 'USD';
    const formatted = (n / 100).toFixed(2);
    const symbols = { USD: '$', EUR: '€', GBP: '£', CNY: '¥', JPY: '¥', CAD: 'C$', AUD: 'A$' };
    const symbol = symbols[currency] || `${currency} `;
    return `${symbol}${formatted}`;
  }

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function buildSizeFilterValue(sizeCtx, wrapper) {
    if (sizeCtx && window.sizeConverter && typeof window.sizeConverter.convertSize === 'function') {
      const converted = window.sizeConverter.convertSize(sizeCtx.system, sizeCtx.size);
      if (converted) {
        const parts = [];
        if (converted['US-M']) parts.push(`US-M ${converted['US-M']}`);
        if (converted['US-W']) parts.push(`US-W ${converted['US-W']}`);
        if (converted['EU']) parts.push(`EU ${converted['EU']}`);
        if (converted['UK']) parts.push(`UK ${converted['UK']}`);
        if (converted['CM']) parts.push(`${converted['CM']}cm`);
        if (converted['IN']) parts.push(`${converted['IN']}"`);
        if (parts.length) return parts.join(' / ');
      }
    }

    const fallback = wrapper?.dataset?.variantTitle || '';
    return String(fallback || '').trim();
  }

  function buildBrowseMoreUrl(sizeCtx, wrapper) {
    const sizeValue = buildSizeFilterValue(sizeCtx, wrapper);
    if (!sizeValue) return '';

    const baseCollectionUrl = new URL('collections/basketball-shoes', getBaseUrl()).toString();
    const url = new URL(baseCollectionUrl);
    const params = new URLSearchParams(url.search);
    params.set('filter.v.option.size us', sizeValue);
    url.search = params.toString();
    return url.toString();
  }

  function buildMoreCardHtml({ url, text, ctaText, ariaLabel }) {
    const safeUrl = escapeHtml(url);
    const safeText = escapeHtml(text);
    const safeCta = escapeHtml(ctaText);
    const safeAria = escapeHtml(ariaLabel || '');

    const arrowIcon = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    return `
      <a class="t4s-notify-reco__card t4s-notify-reco__card--more" href="${safeUrl}"${safeAria ? ` aria-label="${safeAria}"` : ''}>
        <span class="t4s-notify-reco__img">${arrowIcon}</span>
        <span class="t4s-notify-reco__meta">
          <span class="t4s-notify-reco__name">${safeText}</span>
          <span class="t4s-notify-reco__footer"><span class="t4s-notify-reco__cta">${safeCta} &rarr;</span></span>
        </span>
      </a>
    `;
  }

  async function fetchJson(url) {
    const res = await fetch(url, { method: 'GET', credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache' } });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return res.json();
  }

  function findAvailableVariantBySizeFallback(variants, system, size) {
    const sys = String(system || '').toUpperCase();
    const sz = String(size || '').trim();
    if (!sys || !sz) return null;

    const baseText = (v) => `${v?.title || ''} ${v?.option1 || ''} ${v?.option2 || ''} ${v?.option3 || ''}`;
    const sizeToken = escapeRegex(sz);

    const patterns = {
      'US-M': new RegExp(`(?:^|\\b)(?:US\\s*-?\\s*M|M)\\s*${sizeToken}(?![\\d.])`, 'i'),
      'US-W': new RegExp(`(?:^|\\b)(?:US\\s*-?\\s*W|W)\\s*${sizeToken}(?![\\d.])`, 'i'),
      'EU': new RegExp(`(?:^|\\b)EU\\s*${sizeToken}(?![\\d.])`, 'i'),
      'UK': new RegExp(`(?:^|\\b)UK\\s*${sizeToken}(?![\\d.])`, 'i'),
      'CM': new RegExp(`(?:^|\\b)${sizeToken}\\s*cm\\b`, 'i'),
      'IN': new RegExp(`(?:^|\\b)${sizeToken}\\s*\"\\b`, 'i')
    };

    const re = patterns[sys] || new RegExp(`\\b${sizeToken}\\b`, 'i');

    for (const v of variants) {
      if (!v || !v.available) continue;
      if (re.test(baseText(v))) return v;
    }
    return null;
  }

  function findAvailableVariantBySize(productData, system, size) {
    const variants = Array.isArray(productData?.variants) ? productData.variants : [];
    if (variants.length === 0) return null;

    if (window.sizeConverter && typeof window.sizeConverter.detectProductType === 'function') {
      const type = window.sizeConverter.detectProductType(variants);
      if (type !== 'FOOTWEAR') return null;

      try {
        const converted = window.sizeConverter.convertProductVariants(variants);
        const match = window.sizeConverter.findVariantBySize(converted, system, size);
        if (match && match.available) return match;
      } catch (e) {}
    }

    return findAvailableVariantBySizeFallback(variants, system, size);
  }

  function buildCardHtml({ title, url, imageUrl, priceHtml, ctaText }) {
    const safeTitle = escapeHtml(title);
    const safeUrl = escapeHtml(url);
    const safeImg = escapeHtml(imageUrl);
    const safeCta = escapeHtml(ctaText);
    const safePriceHtml = priceHtml || '';

    return `
      <a class="t4s-notify-reco__card" href="${safeUrl}">
        <span class="t4s-notify-reco__img">${safeImg ? `<img src="${safeImg}" alt="${safeTitle}" loading="lazy">` : ''}</span>
        <span class="t4s-notify-reco__meta">
          <span class="t4s-notify-reco__name">${safeTitle}</span>
          <span class="t4s-notify-reco__footer">${safePriceHtml ? `<span class="t4s-notify-reco__price">${safePriceHtml}</span>` : ''}<span class="t4s-notify-reco__cta">${safeCta} &rarr;</span></span>
        </span>
      </a>
    `;
  }

  async function getHandlesFromRecommendations(baseUrl, productId) {
    const url = new URL(
      `recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${CONFIG.recommendationsLimit}&intent=related`,
      baseUrl
    ).toString();
    const data = await fetchJson(url);
    const products = Array.isArray(data?.products) ? data.products : [];
    return products.map(p => p?.handle).filter(Boolean);
  }

  async function getHandlesFromSuggest(baseUrl, query) {
    if (!query) return [];
    const url = new URL(
      `search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${CONFIG.suggestLimit}&resources[options][unavailable_products]=last`,
      baseUrl
    ).toString();

    const data = await fetchJson(url);
    const products = data?.resources?.results?.products;
    return Array.isArray(products) ? products.map(p => p?.handle).filter(Boolean) : [];
  }

  function uniq(list) {
    const seen = new Set();
    const out = [];
    for (const item of list) {
      if (!item || seen.has(item)) continue;
      seen.add(item);
      out.push(item);
    }
    return out;
  }

  async function refresh() {
    const els = getRecoElements();
    if (!els) return;

    const popup = getPopupRoot();
    const wrapper = popup?.querySelector('.t4s-notify-wrapper');
    
    // 优先从 wrapper 获取，备选从 notify 按钮获取
    const btn = getNotifyButtonBestEffort();
    let productId = wrapper?.dataset.productId || '';
    let productHandle = wrapper?.dataset.productHandle || '';
    let productTitle = wrapper?.dataset.productTitle || wrapper?.querySelector('.t4s-notify-product-name')?.textContent || '';
    
    // 如果 wrapper 数据为空，从按钮获取
    if (!productId && btn) {
      productId = btn.dataset.productId || btn.getAttribute('data-product-id') || '';
    }
    if (!productHandle && btn) {
      productHandle = btn.dataset.productHandle || btn.getAttribute('data-product-handle') || '';
    }
    if (!productTitle && btn) {
      productTitle = btn.dataset.productTitle || btn.getAttribute('data-product-title') || '';
    }

    const sizeCtx = getSelectedSizeContext();
    log.info('refresh context', { productId, productHandle, productTitle, sizeCtx });
    
    if (!productId || !sizeCtx || !isSupportedSizeSystem(sizeCtx.system)) {
      log.warn('missing required data', { productId, sizeCtx });
      els.root.hidden = false;
      renderEmpty(els.list, els.emptyText);
      return;
    }

    const baseUrl = getBaseUrl();
    const modelKey = extractModelKeyFromTitle(productTitle);
    const colorKey = extractColorKeyFromTitle(productTitle);

    const cacheKey = `${productId}|${sizeCtx.system}|${sizeCtx.size}`;
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      els.root.hidden = false;
      if (cached) {
        els.list.innerHTML = cached;
      } else {
        renderEmpty(els.list, els.emptyText);
      }
      return;
    }

    els.root.hidden = false;
    renderSkeleton(els.list);

    const reqId = ++requestCounter;

    try {
      const [suggestHandles, recHandles] = await Promise.all([
        getHandlesFromSuggest(baseUrl, modelKey),
        getHandlesFromRecommendations(baseUrl, productId)
      ]);
      if (reqId !== requestCounter) return;

      const candidates = uniq([...suggestHandles, ...recHandles]).filter(h => h !== productHandle).slice(0, 36);
      if (candidates.length === 0) {
        cache.set(cacheKey, '');
        renderEmpty(els.list, els.emptyText);
        // 追踪：无推荐结果
        trackEvent('oos_reco_empty', {
          product_id: productId,
          product_handle: productHandle,
          product_title: productTitle,
          size_system: sizeCtx.system,
          size_value: sizeCtx.size,
          candidates_count: candidates.length
        });
        // 同步到 NotifyTracker
        notifyTrackerLoaded(0);
        return;
      }

      const desiredModelKey = normalizeKey(modelKey);
      const desiredColorKey = normalizeKey(colorKey || '');

      const matches = [];
      const batchSize = CONFIG.batchSize;

      for (let i = 0; i < candidates.length && matches.length < CONFIG.maxCards; i += batchSize) {
        const batch = candidates.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(handle => fetchJson(new URL(`products/${handle}.js`, baseUrl).toString()))
        );
        if (reqId !== requestCounter) return;

        for (const r of results) {
          if (r.status !== 'fulfilled') continue;

          const p = r.value;
          if (!p || !p.handle) continue;

          const matchVariant = findAvailableVariantBySize(p, sizeCtx.system, sizeCtx.size);
          if (!matchVariant) continue;

          const pColorKey = normalizeKey(extractColorKeyFromTitle(p.title) || '');
          const pModelKey = normalizeKey(extractModelKeyFromTitle(p.title));

          const score =
            (desiredColorKey && pColorKey && pColorKey === desiredColorKey ? 2 : 0) +
            (desiredModelKey && pModelKey && pModelKey === desiredModelKey ? 1 : 0);

          const img = p.featured_image || (Array.isArray(p.images) ? p.images[0] : '');
          const imageUrl = img ? toSizedImageUrl(img, 160) : '';
          const url = new URL(`products/${p.handle}?variant=${matchVariant.id}`, baseUrl).toString();
          const rawPrice = matchVariant.price ?? p.price;
          const rawCompare = matchVariant.compare_at_price ?? p.compare_at_price;
          const moneyPrice = formatMoneySafe(rawPrice);
          const moneyCompare = formatMoneySafe(rawCompare);
          let priceHtml = '';

          if (moneyPrice) {
            if (Number.isFinite(Number(rawCompare)) && Number.isFinite(Number(rawPrice)) && rawCompare > rawPrice) {
              priceHtml = `<del>${escapeHtml(moneyCompare)}</del> <ins>${escapeHtml(moneyPrice)}</ins>`;
            } else {
              priceHtml = escapeHtml(moneyPrice);
            }
          }

          matches.push({
            score,
            html: buildCardHtml({ title: p.title, url, imageUrl, priceHtml, ctaText: els.ctaText })
          });
          if (matches.length >= CONFIG.maxCards) break;
        }
      }

      if (matches.length === 0) {
        cache.set(cacheKey, '');
        renderEmpty(els.list, els.emptyText);
        // 追踪：无推荐结果
        trackEvent('oos_reco_empty', {
          product_id: productId,
          product_handle: productHandle,
          product_title: productTitle,
          size_system: sizeCtx.system,
          size_value: sizeCtx.size,
          candidates_count: candidates.length
        });
        // 同步到 NotifyTracker
        notifyTrackerLoaded(0);
        return;
      }

      matches.sort((a, b) => b.score - a.score);

      let html = matches.map(m => m.html).join('');

      const moreUrl = buildBrowseMoreUrl(sizeCtx, wrapper);
      if (moreUrl) {
        const ariaLabel = sizeCtx && sizeCtx.system && sizeCtx.size
          ? `Browse more in ${sizeCtx.system} ${sizeCtx.size}`
          : '';
        html += buildMoreCardHtml({
          url: moreUrl,
          text: els.moreText,
          ctaText: els.ctaText,
          ariaLabel
        });
      }

      cache.set(cacheKey, html);
      els.list.innerHTML = html;

      // 追踪：推荐展示
      trackEvent('oos_reco_shown', {
        product_id: productId,
        product_handle: productHandle,
        product_title: productTitle,
        size_system: sizeCtx.system,
        size_value: sizeCtx.size,
        recommendations_count: matches.length
      });
      // 同步到 NotifyTracker
      notifyTrackerLoaded(matches.length);

      log.info('rendered', { productId, productHandle, sizeCtx, modelKey, colorKey, count: matches.length });
    } catch (e) {
      log.warn('failed to load recommendations', e);
      cache.set(cacheKey, '');
      renderEmpty(els.list, els.emptyText);
    }
  }

  function init() {
    // Expose minimal hook for debugging from DevTools.
    window.T4SNotifyStockReco = {
      version: VERSION,
      refresh: scheduleRefresh,
      _debug: () => ({
        popupFound: !!getPopupRoot(),
        recoFound: !!getRecoElements(),
        sizeCtx: getSelectedSizeContext(),
        baseUrl: getBaseUrl(),
        wrapperData: (() => {
          const popup = getPopupRoot();
          const wrapper = popup?.querySelector('.t4s-notify-wrapper');
          return wrapper ? { ...wrapper.dataset } : null;
        })(),
        recoHidden: (() => {
          const els = getRecoElements();
          return els ? !!els.root.hidden : null;
        })()
      })
    };

    flushPendingEvents();

    const $ = window.jQuery || window.$ || window.jQuery_T4NT;
    if ($ && $.magnificPopup) {
      $(document).on('mfpOpen', () => {
        // Wait for AJAX content injection
        setTimeout(scheduleRefresh, 50);
        setTimeout(scheduleRefresh, 250);
      });
    }

    // 2) Click on notify buttons (covers non-mfp flows too)
    document.addEventListener(
      'click',
      (e) => {
        const el = e.target && e.target.closest ? e.target.closest('[data-notify-stock-btn]') : null;
        if (!el) return;
        setTimeout(scheduleRefresh, 0);
        setTimeout(scheduleRefresh, 200);
      },
      true
    );

    // 2.5) 追踪推荐卡片点击
    document.addEventListener(
      'click',
      (e) => {
        const card = e.target && e.target.closest ? e.target.closest('.t4s-notify-reco__card') : null;
        if (!card) return;
        
        const popup = getPopupRoot();
        const wrapper = popup?.querySelector('.t4s-notify-wrapper');
        const btn = getNotifyButtonBestEffort();
        const sizeCtx = getSelectedSizeContext();
        
        // 获取来源产品信息
        const sourceProductId = wrapper?.dataset.productId || btn?.dataset.productId || '';
        const sourceProductHandle = wrapper?.dataset.productHandle || btn?.dataset.productHandle || '';
        
        // 获取点击的推荐产品信息
        const recoUrl = card.getAttribute('href') || '';
        const recoTitle = card.querySelector('.t4s-notify-reco__name')?.textContent || '';
        const recoPrice = card.querySelector('.t4s-notify-reco__price')?.textContent || '';
        
        // 获取点击位置（第几个推荐）
        const list = card.closest('[data-oos-reco-list]');
        const cards = list ? Array.from(list.querySelectorAll('.t4s-notify-reco__card')) : [];
        const position = cards.indexOf(card) + 1;
        
        trackEvent('oos_reco_click', {
          source_product_id: sourceProductId,
          source_product_handle: sourceProductHandle,
          size_system: sizeCtx?.system || '',
          size_value: sizeCtx?.size || '',
          reco_title: recoTitle,
          reco_price: recoPrice,
          reco_url: recoUrl,
          reco_position: position,
          total_recommendations: cards.length
        }, true);
        
        // 同步到 NotifyTracker
        if (window.NotifyTracker) {
          window.NotifyTracker.onRecoClick({ recoTitle, recoUrl, position });
        }
      },
      true
    );

    // 3) Observe class changes on mfp wrapper (fallback)
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes') {
            const target = m.target;
            if (!target || !target.classList) continue;
            if (target.classList.contains('mfp-wrap') || target.classList.contains('mfp-ready')) {
              setTimeout(scheduleRefresh, 60);
              return;
            }
          }

          if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
            for (const node of m.addedNodes) {
              if (!node || node.nodeType !== 1) continue;
              if (node.id === 't4s-pr-popup__notify-stock' || (node.querySelector && node.querySelector('#t4s-pr-popup__notify-stock'))) {
                setTimeout(scheduleRefresh, 60);
                return;
              }
              if (node.querySelector && node.querySelector('[data-oos-reco]')) {
                setTimeout(scheduleRefresh, 60);
                return;
              }
            }
          }
        }
      });

      observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    }

    // 4) Initial (in case popup is already in DOM)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(scheduleRefresh, 0));
    } else {
      setTimeout(scheduleRefresh, 0);
    }

    // 5) Poll while popup is open (fallback for edge cases)
    let lastKey = '';
    setInterval(() => {
      const popup = getPopupRoot();
      if (!popup) return;

      const style = window.getComputedStyle(popup);
      if (popup.hidden || style.display === 'none' || style.visibility === 'hidden') return;

      const wrapper = popup.querySelector('.t4s-notify-wrapper');
      const productId = wrapper?.dataset.productId || '';
      const sizeCtx = getSelectedSizeContext();
      if (!productId || !sizeCtx) return;

      const key = `${productId}|${sizeCtx.system}|${sizeCtx.size}`;
      const els = getRecoElements();
      if (key && key !== lastKey) {
        lastKey = key;
        scheduleRefresh();
        return;
      }

      if (els && els.root.hidden) scheduleRefresh();
    }, 500);
  }

  init();
})();
